import axios from "axios";
import Vue from "vue";
import { backend, requireSponsorUrl } from "../.config";
import { compareModTs } from "./helpers";
import { MemberData } from "./interfaces";
import { MediaItem } from "./models";
import {
  emptyString,
  filterLink,
  filterLinkTitle,
  filterStatement,
  isNumeric,
  notEmptyLinkField,
  notEmptyString,
} from "./validators";

const fromLocal = (key: string) => {
  const value = Vue.ls.get(key);
  const result = { valid: false, data: null };
  if (value) {
    result.data = value;
    result.valid = true;
  }
  return result;
};

const toLocal = (key: string, data = null, expirySecs = 86400) => {
  Vue.ls.set(key, data, expirySecs * 1000);
};

const randChar = (chars) => {
  const len = chars.length;
  const randIndex = Math.floor(Math.random() * len * 0.9999999);
  return chars[randIndex];
};

const randInt36 = (power = 3) => {
  const randInt = Math.floor(
    Math.random() * Math.pow(10, power) * 0.9999999999
  );
  return randInt.toString(36);
};

const hexDecStringToBase36Parts = (hexDecStr) => {
  return [hexDecStr.substring(0, 12), hexDecStr.substring(12)]
    .map((hd) => parseInt(hd, 16).toString(36))
    .join("_");
};

export const toDynamicKey = (uid = "") => {
  const { key, suffixSplitChars } = backend;
  const addUid = uid.length > 4;
  const ts = new Date().getTime();
  const tsList = ts
    .toString(36)
    .split("")
    .reverse();
  const offset = (parseInt(tsList[0], 36) % 6) + 1;
  const uidComponent = addUid
    ? [hexDecStringToBase36Parts(uid), randInt36(3)].join(
        randChar(suffixSplitChars)
      )
    : "";
  const mergedList = tsList.map((ch, index) =>
    index === offset ? ch + key : ch
  );
  const baseStr = [mergedList.join(""), randInt36(3)].join(
    randChar(suffixSplitChars)
  );
  const keyStr = addUid ? [baseStr, uidComponent].join("__") : baseStr;
  return btoa(keyStr);
};

export const extractUserId = (params = null) => {
  let userId = "";
  if (params instanceof Object) {
    Object.entries(params).forEach((entry) => {
      const [key, value] = entry;
      switch (key) {
        case "userId":
        case "userID":
        case "user":
          if (typeof value === "string") {
            userId = value;
          }
          break;
      }
    });
  }
  return userId;
};

export const buildOptions = (
  isFormData = false,
  userId = null,
  callback = null
) => {
  const { key, httpAuth } = backend;
  const options: any = {};
  const headers: any = {};
  const uid = notEmptyString(userId) ? userId : "";
  if (isFormData) {
    headers["X-Requested-With"] = "XMLHttpRequest";
    headers["Content-Type"] = "multipart/form-data";
    if (callback instanceof Function) {
      options.onUploadProgress = callback;
    }
  }

  if (httpAuth) {
    if (notEmptyString(httpAuth.user, 3) && notEmptyString(httpAuth.pass, 3)) {
      const credentials = btoa(httpAuth.user + ":" + httpAuth.pass);
      headers.Authorization = "Basic " + credentials;
    }
  }
  if (key) {
    if (key.length > 1) {
      headers.token = toDynamicKey(uid);
    }
  }
  options.headers = headers;
  return options;
};

export const buildQueryString = (criteria = null, postMode = false) => {
  let str = "";
  if (criteria instanceof Object) {
    const parts: Array<string> = [];
    Object.entries(criteria).forEach((entry) => {
      const [key, val] = entry;
      let paramVal = val;
      if (typeof val === "string") {
        paramVal = encodeURIComponent(val);
      } else if (typeof val === "number" || typeof val === "boolean") {
        paramVal = val.toString();
      } else if (val instanceof Array) {
        paramVal = val.join(",");
      }
      parts.push(key + "=" + paramVal);
    });
    if (!postMode) {
      str = "?";
    }
    if (parts.length > 0) {
      str += parts.join("&");
    }
  }
  return str;
};

const postData = async (
  path: string,
  params = null,
  callback = null,
  putMode = false
) => {
  let result: any = {};
  const isFormData = params instanceof FormData;
  const func = putMode !== true ? axios.post : axios.put;
  const userId = extractUserId(params);
  await func(
    `${backend.api}${path}`,
    params,
    buildOptions(isFormData, userId, callback)
  )
    .then((res) => {
      result = res;
    })
    .catch((res) => {
      result = res;
    });
  return result;
};

export const postRemote = async (url, params) => {
  let result: any = {};
  await axios
    .post(url, params)
    .then((res) => {
      result = res;
    })
    .catch((res) => {
      result = res;
    });
  return result;
};

/* export const getRemote = async (url = "") => {
  let result: any = {};
  await axios
    .get(url)
    .then((res) => {
      result = res;
    })
    .catch((res) => {
      result = res;
    });
  return result;
}; */

const putData = async (path, params, callback = null) => {
  return postData(path, params, callback, true);
};

const fetchData = async (path, deleteMode = false, userId = "") => {
  let result: any = {};
  const func = deleteMode !== true ? axios.get : axios.delete;
  await func(`${backend.api}${path}`, buildOptions(false, userId))
    .then((res) => {
      result = res;
    })
    .catch((res) => {
      result = res;
    });
  return result;
};

export const fetchContent = async (
  path: string,
  refresh = false,
  userId = ""
) => {
  if (refresh === true && typeof path === "string") {
    path += "?refresh=1";
  }
  return fetchData(path, false, userId);
};

export const deleteData = async (path, userId = "") => {
  return fetchData(path, true, userId);
};

export const fetchSiteData = async (mode = "student", refresh = false) => {
  const cKey = ["setting/options", mode].join("/");
  const stored = fromLocal(cKey);
  let data = {
    valid: false,
    settings: [],
    snippets: [],
    courses: [],
    schools: [],
    programmes: [],
    themes: [],
    displayOptions: [],
  };
  let fetchNew = true;
  refresh = refresh === true;
  let isStored = false;
  if (stored.valid) {
    await fetchContent("setting/is-open").then((res) => {
      if (res.data) {
        const od = res.data;
        const {
          settings,
          snippets,
          courses,
          schools,
          programmes,
          displayOptions,
          themes,
        } = stored.data;
        if (settings instanceof Array && courses instanceof Array) {
          if (od.deadline) {
            const openIndex = settings.findIndex(
              (s) => s.key === "submissions_open"
            );
            const dlIndex = settings.findIndex(
              (s) => s.key === "submissions_deadline"
            );
            if (openIndex >= 0 && dlIndex >= 0) {
              settings[openIndex].value = od.open;
              settings[dlIndex].value = od.deadline;
              data = {
                valid: true,
                settings,
                snippets,
                schools,
                programmes,
                courses,
                displayOptions,
                themes,
              };
              fetchNew = false;
              isStored = true;
            }
          }
        }
      }
    });
  }
  if (fetchNew) {
    await fetchContent(cKey, refresh).then((res) => {
      if (res.data instanceof Object) {
        const { settings } = res.data;
        if (settings instanceof Array) {
          data = res.data;
          isStored = false;
        }
      }
    });
  }
  if (!isStored) {
    if (data.settings) {
      toLocal(cKey, data, 86400);
    }
  }
  return data;
};

export const checkIsOpen = async (userId = "") => {
  const qStr = notEmptyString(userId) ? buildQueryString({ uid: userId }) : "";
  const response = await fetchContent("setting/is-open" + qStr);
  if (response) {
    const { data } = response;
    return data;
  } else {
    return { valid: false };
  }
};

export const fetchTags = async (refresh = false) => {
  const cKey = "tags";
  const stored = fromLocal(cKey);
  let tags = [];
  if (stored.valid && !refresh) {
    tags = stored.data;
  } else {
    await fetchContent("submission/tags").then((res) => {
      if (res.data) {
        const { data } = res;
        if (data instanceof Array) {
          if (data.length > 0) {
            toLocal(cKey, data, 900);
            tags = data;
          }
        }
      }
    });
  }
  return tags;
};

const handleLogin = (user, result) => {
  const cKey = "current-user";
  const keys = user instanceof Object ? Object.keys(user) : [];
  if (keys.length > 1 && notEmptyString(user.identifier, 5) && user.active) {
    let su: any = {};
    Object.entries(user).forEach((entry) => {
      const [key, val] = entry;
      switch (key) {
        case "submission":
          if (val instanceof Object) {
            su = val;
            if (su.user instanceof Object) {
              if (su.user._id) {
                su.user = su.user._id;
              }
            }
            result.submission = su;
          }
          break;
        default:
          result[key] = val;
          break;
      }
    });
    if (su instanceof Object) {
      result.hasSubmission = true;
    }
    result.valid = true;
    toLocal(cKey, result, 86400);
  }
  if (keys.includes("isStudent")) {
    result.isStudent = user.isStudent;
  }
  if (keys.includes("isLead")) {
    result.isLead = user.isLead;
  }
  if (user.multifactor || user.startAd) {
    if (user.multifactor) {
      result.multifactor = true;
    }
    if (user.startAd) {
      result.startAd = true;
    }
    if (user.ad) {
      result.ad = user.ad;
    }
  }
  if (user.msg) {
    result.msg = user.msg.toString();
  }
  return result;
};

export const authenticate = async (email: string, password: string) => {
  const result: any = {
    valid: false,
    multifactor: false,
    hasSubmission: false,
    submission: null,
    identifier: "",
    active: false,
    isStudent: false,
    groupMode: false,
    isLead: false,
    ad: null,
    startAd: false,
    key: "ok",
    msg: "",
  };
  const params = {
    email,
    password,
    mode: backend.mode,
  };
  await postData("user/login", params).then((response) => {
    const { data } = response;
    if (data instanceof Object) {
      handleLogin(data, result);
    }
  });
  return result;
};

export const msalAuthenticate = async (payload) => {
  const result: any = {
    valid: false,
    hasSubmission: false,
    submission: null,
    identifier: "",
    active: false,
    isStudent: false,
    groupMode: false,
    isLead: false,
    key: "ok",
    msg: "",
  };
  const response = await postData("user/msal-auth", payload);
  const { data } = response;
  if (data instanceof Object) {
    handleLogin(data, result);
    if (data.msg) {
      result.msg = data.msg;
      result.isStudent = data.isStudent === true;
      result.isLead = data.isLead === true;
      result.key = data.key;
    }
  }
  return result;
};

export const registerLogout = async (userId, slug = "") => {
  const url = ["user/logout", userId, slug].join("/");
  await deleteData(url).then((response) => {
    if (response.data) {
      return response.data;
    }
  });
  return { valid: false };
};

export const updateUser = async (userId, edited) => {
  const data = {
    valid: false,
    user: null,
    msg: "Service not available",
  };
  await putData("user/edit/" + userId, edited).then((response) => {
    if (response.data) {
      const { user } = response.data;
      if (user._id) {
        data.user = user;
      }
    }
  });
  return data;
};

export const resetPassword = async (hash, params) => {
  let data = {
    valid: false,
    msg: "unmatched",
  };
  await putData(`user/reset-pass/` + hash, params).then((res) => {
    if (res.data) {
      if (res.data.identifier) {
        data = res.data;
        if (data.valid) {
          data.msg = "OK";
        } else {
          data.msg = "Account unavailable";
        }
      }
    }
  });
  return data;
};

export const requestReset = async (email) => {
  let data: any = {
    valid: false,
    msg: "unmatched",
  };
  await postData(`user/reset-request`, { email }).then((res) => {
    if (res.data) {
      if (res.data instanceof Object) {
        data = res.data;
        if (data.link) {
          data.valid = true;
        }
      }
    }
  });
  return data;
};

export const saveUser = async (user) => {
  let data: any = {
    valid: false,
    msg: "unmatched",
  };
  await postData("user/save", user).then((res) => {
    if (res.data) {
      if (res.data.identifier) {
        data = res.data;
        if (data.status > 0) {
          data.valid = true;
          data.msg = "OK";
          const cKey = "current-user";
          toLocal(cKey, data, 86400);
        } else {
          data.msg = "Account disabled";
        }
      }
    }
  });
  return data;
};

export const registerUser = async (params) => {
  const data = {
    valid: false,
    user: null,
    msg: "",
  };
  const saveParams = {
    identifier: params.identifier,
    password: params.password,
    mode: "local",
    role: "artist",
    active: true,
  };
  let qStr = "";
  if (params instanceof Object) {
    const { firstName, lastName } = params;
    const parts = [];
    if (firstName) {
      parts.push(["firstName", firstName].join("="));
    }
    if (lastName) {
      parts.push(["lastName", lastName].join("="));
    }
    if (parts.length > 0) {
      qStr = "?" + parts.join("&");
    }
  }
  await postData(`user/create${qStr}`, saveParams).then((res) => {
    if (res.data) {
      if (res.data.user) {
        const { user } = res.data;
        data.msg = res.data.message;
        if (user.active) {
          user.valid = true;
          const cKey = "current-user";
          data.user = user;
          data.valid = true;
          toLocal(cKey, user, 86400);
        }
      }
    }
  });
  return data;
};

export const editUser = async (userId, params) => {
  let data = {
    valid: false,
    user: null,
  };
  await putData(`user/edit/${userId}`, params).then((res) => {
    if (res.data) {
      if (res.data.user) {
        data = res.data;
        if (data.user.active) {
          data.valid = true;
          const cKey = "current-user";
          toLocal(cKey, data, 86400);
        }
      }
    }
  });
  return data;
};

const handleSubmissionResponse = (response, data) => {
  if (response.data) {
    const { submission, message, isNewer, age } = response.data;
    if (submission instanceof Object) {
      if (submission._id) {
        data.submission = submission;
        data.valid = true;
        toLocal("submission", submission);
      }
    }
    if (message) {
      data.msg = message;
      data.isNewer = isNewer;
      data.age = age;
    }
  }
};

export const getResetUser = async (hash) => {
  const response = await fetchContent("user/reset/" + hash);
  let data = {
    valid: false,
    identifier: "",
    uid: 0,
    mode: "",
    login: "",
  };
  if (response) {
    if (response.data) {
      data = response.data;
    }
  }
  return data;
};

export const saveSubmission = async (
  submission,
  programmesLocked = true,
  coursesLocked = true,
  skipWorks = false
) => {
  const data = {
    valid: false,
    submission: null,
    isNewer: false,
    age: 0,
  };
  let mod = 0;
  const groupMode = ["group", "project", "projects"].includes(backend.mode);
  if (submission instanceof Object) {
    const edited = Object.assign({}, submission);
    edited.mode = backend.mode;
    if (emptyString(edited.user)) {
      const cu = Vue.ls.get("user");
      if (cu instanceof Object) {
        if (Object.keys(cu).includes("_id")) {
          const userId = cu._id.toString();
          if (notEmptyString(userId, 12)) {
            edited.user = userId;
          }
        }
      }
    }
    if (emptyString(edited._id)) {
      delete edited._id;
    }
    const subKeys = Object.keys(edited);
    if (compareModTs() && subKeys.includes("modifiedAt")) {
      mod = submission.modifiedTs;
    }
    // do not save these fields via this method
    if (!groupMode) {
      delete edited.slug;
    }
    delete edited.ineligible;
    delete edited.deadlineException;
    delete edited.deadlineEnd;
    // delete edited.checked;
    delete edited.modifiedAt;
    delete edited.createdAt;
    delete edited.submitted;

    const skipCourses = coursesLocked || emptyString(edited.courseCode);
    if (skipCourses) {
      delete edited.courseCode;
      delete edited.course;
    }
    const skipProgrammes =
      groupMode ||
      programmesLocked ||
      emptyString(edited.programmeCode) ||
      emptyString(edited.school);
    if (skipProgrammes) {
      delete edited.programme;
      delete edited.programmeCode;
      delete edited.school;
    }
    const hasWorks = subKeys.includes("works");
    const worksIsArray = hasWorks && edited.works instanceof Array;
    const hasEmptyWorks =
      (worksIsArray && edited.works.length < 1) || (hasWorks && !worksIsArray);
    // do not allow saving of empty work arrays. Assume invalid
    if ((skipWorks && hasWorks) || hasEmptyWorks) {
      delete edited.works;
    }
    const hasStatements = subKeys.includes("statements");
    const hasValidStatements =
      hasStatements &&
      edited.statements instanceof Array &&
      edited.statements.length > 0;
    // do not allow saving of empty statement arrays. Assume invalid
    if (!hasValidStatements && hasStatements) {
      delete edited.statements;
    }

    // always exclude themes in student mode
    if (backend.mode !== "group") {
      delete edited.themes;
    }
    if (subKeys.includes("user")) {
      if (
        subKeys.includes("links") &&
        edited.links instanceof Array &&
        edited.links.length > 0
      ) {
        edited.links = edited.links.filter(filterLink);
      } else {
        delete edited.links;
      }
      if (
        subKeys.includes("sponsors") &&
        edited.sponsors instanceof Array &&
        edited.sponsors.length > 0
      ) {
        const sponsorLinkValidator = requireSponsorUrl
          ? filterLink
          : notEmptyLinkField;
        edited.sponsors = edited.sponsors.filter(sponsorLinkValidator);
      } else {
        delete edited.sponsors;
      }
      if (
        subKeys.includes("statements") &&
        edited.statements instanceof Array
      ) {
        edited.statements = edited.statements.filter(filterStatement);
      }
      if (hasWorks && edited.works instanceof Array) {
        edited.works = edited.works.filter((w) => w instanceof Object);
      }
      if (edited._id) {
        const url =
          ["submission/edit", edited._id].join("/") + buildQueryString({ mod });
        await putData(url, edited).then((response) =>
          handleSubmissionResponse(response, data)
        );
      } else {
        await postData("submission/create", edited).then((response) =>
          handleSubmissionResponse(response, data)
        );
      }
    }
  }
  return data;
};

export const saveWork = async (submissionId = "", work = null, mod = 0) => {
  const data = {
    valid: false,
    work: null,
    msg: "",
    modifiedAt: null,
  };
  let isNew = true;
  let valid = false;
  if (work instanceof Object) {
    const edited = Object.assign({}, work);
    if (edited._id) {
      isNew = notEmptyString(edited._id, 12) === false;
      valid = notEmptyString(edited.title);
    } else {
      valid = Object.keys(edited).length > 0;
    }
    if (valid) {
      const verb = isNew ? "add-work" : "edit-work";
      const parts = ["submission", verb, submissionId];
      if (!isNew) {
        parts.push(edited._id);
      }
      const path = parts.join("/") + buildQueryString({ mod });
      if (edited.links instanceof Array) {
        edited.links = edited.links.filter(filterLinkTitle);
      }
      if (edited.mediaItems instanceof Array) {
        edited.mediaItems = edited.mediaItems
          .filter((m) => m instanceof MediaItem && m.isValid)
          .map((m) => {
            const mi = { ...m };
            if (!notEmptyString(m._id, 12)) {
              delete mi._id;
            }
            return mi;
          });
      }
      await putData(path, { ...edited }).then((response) => {
        if (response.data) {
          const { work, message, modifiedAt } = response.data;
          if (work) {
            data.work = work;
            data.valid = work instanceof Object;
            data.modifiedAt = modifiedAt;
          }
          data.msg = message;
        }
      });
    }
  }
  return data;
};

const filterVimeoData = (fileInfo) => {
  const data: any = {};
  Object.entries(fileInfo).forEach((entry) => {
    const [key, value] = entry;
    switch (key) {
      case "width":
      case "height":
      case "duration":
        data[key] = value;
        break;
      case "thumbnail_large":
        data.preview = value;
        break;
      case "upload_date":
        if (typeof value === "string") {
          data.uploadDate = new Date(value);
        }
        break;
    }
  });
  return data;
};

export const saveResource = async (
  submissionId = "",
  workId = "",
  fileInfo = null,
  source = "",
  mod = 0
) => {
  let data: any = { valid: false };
  const isPlayer = source === "player";
  const filename = isPlayer ? fileInfo.filename : "vimeo://" + fileInfo.id;
  const sourceFilename = isPlayer ? fileInfo.sourceFilename : fileInfo.url;
  const title = isPlayer ? "" : fileInfo.title;
  const mime = isPlayer ? fileInfo.mime : "video/vimeo";
  const type = isPlayer ? fileInfo.type : "video";
  const attributes = isPlayer ? {} : filterVimeoData(fileInfo);
  const mediaItem = {
    source,
    filename,
    sourceFilename,
    title,
    mime,
    type,
    attributes,
  };
  const url =
    ["submission/add-file", submissionId, workId].join("/") +
    buildQueryString({ mod });
  await postData(url, mediaItem).then((response) => {
    if (response.data) {
      data = response.data;
      data.valid = true;
    }
  });
  return data;
};

export const saveFile = async (
  submissionId: string,
  workId: string,
  file,
  callback = null,
  mod = 0
) => {
  let result: any = { valid: false, msg: "" };
  if (submissionId && workId) {
    const formData = new FormData();
    formData.set("file", file);
    const url =
      ["submission/upload", submissionId, workId].join("/") +
      buildQueryString({ mod });
    const response = await postData(url, formData, callback);
    const { data } = response;
    const invalidMsg = `Invalid file type.`;
    if (data instanceof Object) {
      if (data.error) {
        result.error = data.error;
        result.msg = invalidMsg;
      } else if (data.type) {
        result = data;
      } else {
        result.msg = invalidMsg;
      }
    } else {
      result.msg = invalidMsg;
    }
  }
  return result;
};

/* export const editMediaItem = async (
  submissionId: string,
  workId: string,
  item = null
) => {
  const itemId = item._id;
  const uri = ["submission", "edit-file", submissionId, workId, itemId].join(
    "/"
  );
  const response = await putData(uri, item);
  if (response.data) {
    return response.data;
  } else {
    return {
      valid: false,
    };
  }
}; */

export const getRemoteSubmission = async (userId: string) => {
  const cKey = "submission/by-user/" + userId;
  let data = {
    valid: false,
  };
  await fetchContent(cKey).then((res) => {
    if (res.data) {
      if (res.data._id) {
        data = res.data;
        data.valid = true;
      }
    }
  });
  return data;
};

export const removeMediaItem = async (
  submissionId: string,
  workId: string,
  itemId: string
) => {
  const uri = ["submission", "remove-file", submissionId, workId, itemId].join(
    "/"
  );
  const response = await putData(uri, { id: itemId });
  if (response.data) {
    return response.data;
  } else {
    return {
      valid: false,
    };
  }
};

export const publishSubmission = async (submissionId: string) => {
  const uri = ["submission", "sync", "cloudinary", submissionId, "submit"].join(
    "/"
  );
  const response = await fetchContent(uri);
  if (response.data) {
    return { ...response.data, valid: true };
  } else {
    return {
      valid: false,
    };
  }
};

export const testSlug = async (slug: string, subId = "") => {
  const result = {
    valid: false,
    suggested: "",
    original: slug,
    conflicts: false,
    validFormat: false,
  };
  const uriParts = ["submission", "test-slug", slug, subId];
  await fetchContent(uriParts.join("/")).then((res) => {
    if (res.data) {
      const { data } = res;
      if (data instanceof Object) {
        result.suggested = data.suggested;
        result.original = data.slug;
        result.valid = data.valid;
        if (data.validFormat) {
          result.validFormat = data.validFormat;
        }
        if (data.conflicts) {
          result.conflicts = data.conflicts;
        }
      }
    }
  });
  return result;
};

/* export const activateAccount = async (key) => {
  let data = {
    valid: false,
  };
  const params = {
    activationCode: key,
  };
  await postData("activate", params).then((res) => {
    if (res.data) {
      data = res.data;
      toLocal("current-user", data, 86400);
    }
  });
  return data;
}; */

export const getCurrentUser = () => {
  let user = { _id: "", valid: false };
  const stored = fromLocal("current-user");
  if (stored.valid) {
    user = stored.data;
    if (user._id) {
      user.valid = true;
    }
  }
  return user;
};

export const getSubmission = () => {
  let submission = { valid: false };
  const stored = fromLocal("submission");
  if (stored.valid) {
    if (stored.data._id) {
      submission = stored.data;
      submission.valid = true;
    }
  }
  return submission;
};

/* export const setCurrentUser = (user) => {
  toLocal("current-user", user);
  if (user.submission) {
    if (user.submission instanceof Object) {
      const { submission } = user;
      if (submission.user instanceof Object) {
        submission.user = submission.user._id;
      }
      toLocal("submission", submission);
    }
  }
};

export const unsetCurrentUser = () => {
  Vue.ls.remove("current-user");
  Vue.ls.remove("submission");
}; */

export const getDataList = async (cKey, mapFunc, refresh = false) => {
  const stored = fromLocal(cKey);
  let dataList = [];
  let fetchNew = true;
  let isStored = false;
  if (stored.valid) {
    dataList = stored.data;
    fetchNew = refresh;
    isStored = true;
  }
  if (fetchNew) {
    await fetchContent(cKey, refresh).then((res) => {
      if (res.data) {
        if (res.data instanceof Array) {
          dataList = res.data.map(mapFunc);
        }
      }
    });
  }
  if (!isStored) {
    if (dataList.length > 0) {
      toLocal(cKey, dataList, 7 * 24 * 60 * 60);
    }
  }
  return dataList;
};

export const checkRemoteUpdatedSub = async (subId = "", mod = 0) => {
  const url = ["submission", "check-remote-updates", subId, mod].join("/");
  const response = await fetchContent(url);
  if (response) {
    const { data } = response;
    return data;
  } else {
    return { valid: false };
  }
};

export const fetchImageMeta = async (
  imageRef = "",
  subId = "",
  mediaRef = ""
) => {
  const uri = ["submission", "test-computer-vision"].join("/");
  const m = imageRef.split("/").pop();
  const r = [subId, mediaRef].join("/");
  const qStr = buildQueryString({ m, r });
  const response = await fetchContent(uri + qStr);
  if (response instanceof Object && response.status === 200) {
    const { data } = response;
    if (data instanceof Object) {
      return data;
    }
  }
  return { valid: false };
};

export const getMsalDetails = async () => {
  const url = ["user", "msal-details"].join("/");
  const response = await fetchContent(url);
  if (response instanceof Object) {
    const { data } = response;
    if (data instanceof Object) {
      return data;
    }
  }
  return { valid: false };
};

export const getVersion = () => {
  const stored = fromLocal("version");
  let version = -1;
  if (stored.valid) {
    if (isNumeric(stored.data)) {
      version = parseFloat(stored.data);
    }
  }
  return version;
};

export const setVersion = (version) => {
  if (isNumeric(version)) {
    version = parseFloat(version);
    toLocal("version", version);
  }
};

export const checkMember = async (identifier = "", subId = "") => {
  const result: MemberData = { valid: false };
  if (notEmptyString(identifier)) {
    const url = ["user", "check-member", identifier, subId].join("/");
    const res = await fetchContent(url);
    if (res instanceof Object) {
      const { data } = res;
      if (data instanceof Object) {
        const { studentSub, otherProject } = data;
        if (studentSub instanceof Object) {
          result.studentSub = studentSub;
          result.otherProject = otherProject;
          result.valid = true;
        }
      }
    }
  }
  return result;
};

export const getVimeoData = async (mediaId: string) => {
  const url = "https://vimeo.com/api/v2/video/" + mediaId + ".json";
  let data: any = {
    valid: false,
  };
  await axios.get(url).then((res) => {
    if (res.data) {
      if (res.data instanceof Array && res.data.length > 0) {
        const item = res.data[0];
        if (item instanceof Object) {
          if (item.url) {
            data = item;
            data.source = "vimeo";
            data.valid = true;
          }
        }
      }
    }
  });
  return data;
};
